import React from 'react';
import { styled } from '@mui/material/styles';
import { UseFormReturn } from 'react-hook-form';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Box, Divider } from '@mui/material';
import TextInput from '../../forms/TextInput';
import DateInput from '../../forms/DateInput';
import AddressInput from '../../forms/AddressInput';
import PhoneInput from '../../forms/PhoneInput';
import SelectMenu from '../../forms/SelectMenu';
import { Client } from '../../../data/models/client.model';
import { PassengerAuthorizationReason } from '../../../data/models/passenger-authorization-reason.model';
import { Gender } from '../../../data/models/gender.model';
import * as clientSlice from '../../../data/client.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import Checkbox from '../../forms/Checkbox';
import { DateTime } from 'luxon';
import ChipSelectMenu from '../../forms/ChipSelectMenu';

const PREFIX = 'ProfileForm';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  paper: `${PREFIX}-paper`,
  form: `${PREFIX}-form`,
  purpose: `${PREFIX}-purpose`,
  subctrl: `${PREFIX}-subctrl`,
  addButton: `${PREFIX}-addButton`,
  mapWrapper: `${PREFIX}-mapWrapper`,
  phone: `${PREFIX}-phone`,
  nameRow: `${PREFIX}-nameRow`,
  nameField: `${PREFIX}-nameField`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    paddingBottom: theme.spacing(8),
    marginBottom: theme.spacing(8),
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
  },

  [`& .${classes.paper}`]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
    gap: theme.spacing(2),
    width: '100%',
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.purpose}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },

  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(4),
  },

  [`& .${classes.addButton}`]: {
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.phone}`]: {
    maxWidth: 260,
  },

  [`& .${classes.nameRow}`]: {
    display: 'flex',
    gap: theme.spacing(2),
  },

  [`& .${classes.nameField}`]: {
    flex: 1,
  },
}));

interface ProfileFormProps {
  currentClientBC: string | undefined;
  portalClientBC: string | undefined;
  clients: Client[];
  genders: Gender[];
  passengerAuthorizationReasons: PassengerAuthorizationReason[];
  formHook: UseFormReturn<any, any>;
}

export default function ProfileForm({
  currentClientBC,
  portalClientBC,
  formHook,
  clients,
  genders,
  passengerAuthorizationReasons,
}: ProfileFormProps) {
  const t = useCommonTranslation();
  const requiredFieldMessage = t('common.requiredField');
  const dispatch = useAppDispatch();

  const { control, formState, watch, setValue } = formHook;

  const clientBC = watch('Client');
  const servicePlans = useAppSelector(clientSlice.selectManualServicePlanList);
  const addressStruct = formHook.watch('AddressStruct');

  React.useEffect(() => {
    if (addressStruct) {
      const addr = JSON.parse(addressStruct);
      if (addr.addressLine2) {
        formHook.setValue('AddressLine2', addr.addressLine2);
      }
    }
  }, [addressStruct]);

  React.useEffect(() => {
    if (clientBC) {
      if (clientBC !== currentClientBC) {
        setValue('ServicePlans', []);
      }
      dispatch(clientSlice.fetchManualClientServicePlansAsync(clientBC));
    }

    return () => {
      dispatch(clientSlice.setManualServicePlanList(null));
    };
  }, [clientBC]);

  React.useEffect(() => {
    if (portalClientBC) {
      setValue('Client', portalClientBC);
    }

    return () => {
      dispatch(clientSlice.setManualServicePlanList(null));
    };
  }, [portalClientBC]);

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.form}>
        {!portalClientBC && clients && clients.length >= 0 && (
          <SelectMenu
            label={t('passenger.client')}
            name="Client"
            values={clients}
            valueAttr="clientBC"
            labelAttr="legalName"
            required={true}
            control={control}
            rules={{ required: requiredFieldMessage }}
          />
        )}
        <Box className={classes.nameRow}>
          <TextInput
            className={classes.nameField}
            name="FirstName"
            label={t('passenger.firstName')}
            control={control}
            required={true}
            rules={{ required: requiredFieldMessage }}
          />
          <TextInput
            className={classes.nameField}
            name="MiddleName"
            label={t('passenger.middleName')}
            control={control}
            required={false}
          />
        </Box>
        <TextInput
          name="LastName"
          label={t('passenger.lastName')}
          control={control}
          required={true}
          rules={{ required: requiredFieldMessage }}
        />
        <DateInput
          label={t('passenger.dateOfBirth')}
          name="DOB"
          control={control}
          required={true}
          errors={formState.errors}
          minDate={DateTime.fromISO('1900-01-01')}
          maxDate={DateTime.now()}
        />
        <SelectMenu
          label={t('passenger.gender')}
          name="Gender"
          values={genders}
          valueAttr="genderCode"
          labelAttr="genderName"
          required={false}
          control={control}
        />
        <TextInput
          name="PassengerID"
          label={t('passenger.passengerId')}
          control={control}
          required={true}
          rules={{ required: requiredFieldMessage }}
          inputProps={{ maxLength: 50 }}
        />
        {/* <TextInput
          name="ServicePlanID"
          label={t('passenger.servicePlanId')}
          control={control}
          required={false}
        /> */}
        {servicePlans && (
          <ChipSelectMenu
            label={t('passenger.servicePlan')}
            name="ServicePlans"
            values={servicePlans ? servicePlans.servicePlans : []}
            valueAttr="servicePlanUUID"
            labelAttr="clientServicePlanName"
            required={true}
            control={control}
            rules={{ required: requiredFieldMessage }}
          />
        )}
        <AddressInput
          name="Address"
          label={t('passenger.address')}
          required={false}
          watch={watch}
          setValue={setValue}
        />
        <TextInput
          name="AddressLine2"
          label={t('passenger.addressLine2')}
          control={control}
          required={false}
        />
        <PhoneInput
          className={classes.phone}
          label={t('passenger.phone')}
          name="Phone"
          extensionName="PhoneExtension"
          control={control}
          formHook={formHook}
          errors={formHook.formState.errors}
        />
        <PhoneInput
          className={classes.phone}
          label={t('passenger.mobilePhone')}
          name="Mobile"
          extensionName="MobileExtension"
          control={control}
          formHook={formHook}
          errors={formHook.formState.errors}
        />
        <Checkbox
          name="SMSConsent"
          label={t('passenger.smsConsent')}
          control={formHook.control}
        />
        <TextInput
          name="Email"
          label={t('passenger.email')}
          control={control}
          disabled={false}
          required={false}
        />
        <TextInput
          name="AuthorizedFirstName"
          label={t('passenger.authorizedFirstName')}
          control={control}
          required={true}
          rules={{ required: requiredFieldMessage }}
        />
        <TextInput
          name="AuthorizedLastName"
          label={t('passenger.authorizedLastName')}
          control={control}
          required={true}
          rules={{ required: requiredFieldMessage }}
        />
        <SelectMenu
          label={t('passenger.createReason')}
          name="CreateReason"
          values={passengerAuthorizationReasons}
          valueAttr="passengerAuthorizationReasonBC"
          labelAttr="passengerAuthorizationReasonName"
          required={true}
          control={control}
          rules={{ required: requiredFieldMessage }}
        />
      </Box>
    </StyledBox>
  );
}
